import React, { useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Alert, Col, Row, Tag, Typography, Card, Flex, Segmented, Grid } from "antd";
import { ExclamationCircleOutlined, ClockCircleOutlined, PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useAntdTheme, useReconnectingWebSocket } from "../../shared/hooks";

import { PendingOrderTabs } from "./PendingOrderTabs";
import StartedOrderCol from "./StartedOrderCol";
import { shortDisplayTime } from "../../shared/utils/TypedUtil";
import { useResponsiveFontSize } from "./hooks/useResponsiveFontSize";
import { useLabelAutoPrintStatus } from "./hooks/useLabelAutoPrintStatus";

import { getOrderState, WS_URL, getOrderTypeDisplay, STATUS } from "../../shared/utils/Util";

const { Paragraph } = Typography;

const getItemActionStatus = (item) => {
  let allPending = true;
  let allDone = true;
  for (let i = 0; i < item.actions.length; i++) {
    const action = item.actions[i];
    if (!action.pending_time && !action.start_time && !action.done_time) {
      // Each action for each item in started order should always have at least
      // pending_time set.
      return STATUS.UNDEFINED;
    } else if (action.pending_time && !action.start_time && !action.done_time) {
      allDone = false;
    } else if (action.pending_time && action.start_time && !action.done_time) {
      allPending = false;
      allDone = false;
    } else {
      // action.pending_time && action.start_time && action.done_time
      allPending = false;
    }
  }
  if (allPending) {
    return STATUS.PENDING;
  }
  if (allDone) {
    return STATUS.DONE;
  }
  return STATUS.STARTED;
};

const getNumItemsByStatus = (order) => {
  let percDoneActions = 0;
  let itemActionStatuses = new Map();
  if (order.items && order.items.length > 0) {
    let numActions = 0;
    let numDoneActions = 0;
    order.items.forEach((item) => {
      let itemActionStatus = STATUS.DONE; // Default status for items with no actions
      if (item.actions) {
        itemActionStatus = getItemActionStatus(item);
        numActions += item.actions.length;
        item.actions.forEach((action) => {
          if (action.done_time) {
            numDoneActions++;
          }
        });
      }
      itemActionStatuses.set(item.id, itemActionStatus);
    });
    percDoneActions = Math.trunc((numDoneActions / numActions) * 100);
  }
  return { itemActionStatuses, percDoneActions };
};

const collapseItems = (order) => {
  if (!order.items || order.items.length <= 0) {
    return { items: order.items };
  }
  const itemMap = new Map();
  order.items.forEach((item) => {
    const itemKey = item.attributes_id;
    if (!itemMap.has(itemKey)) {
      itemMap.set(itemKey, {
        ...item,
        id: itemKey,
        itemIDs: new Set(),
        qty: 0,
        redeemed: false,
      });
    }
    const collapsedItem = itemMap.get(itemKey);
    collapsedItem.redeemed |= item.redeemed;
    collapsedItem.qty++;
    collapsedItem.itemIDs.add(item.id);
  });
  return { items: Array.from(itemMap.values()) };
};

// delta is an OrderUpdate object from the backend.
// https://github.com/TicketRails/backend/blob/8dfdecdf5c272c2969384e468a0ab6ee0ae8cb3c/services/api/websocket/schema.go#L9
const updateOrdersByState = (oldState, delta) => {
  var newState = { ...oldState };

  // Get all the order IDs that changed
  var changedIDs = new Set();
  if (delta.updated) {
    delta.updated.forEach((order) => {
      changedIDs.add(order.id);
    });
  }
  if (delta.deleted) {
    delta.deleted.forEach((id) => changedIDs.add(id));
  }

  // Filter out done orders before 30 minutes ago, cancelled orders, and
  // remove all changed orders from the new state.
  const doneAfter = new Date();
  doneAfter.setTime(doneAfter.getTime() - 30 * 60 * 1000);
  ["pending", "started", "done"].forEach((k) => {
    const removedIndices = new Set();
    for (let i = 0; i < oldState[k].length; i++) {
      const order = oldState[k][i];
      if (
        changedIDs.has(order.id) ||
        (order.done_time && new Date(order.done_time) < doneAfter) ||
        order.cancel_time
      ) {
        removedIndices.add(i);
      }
    }

    newState[k] = newState[k].filter((order, i) => !removedIndices.has(i));
  });

  // Add all updated orders.
  if (delta.updated) {
    delta.updated.forEach((order) => {
      const k = getOrderState(order);
      if (k === "started") {
        order = {
          ...order,
          ...getNumItemsByStatus(order),
        };
      } else if (k === "canceled") {
        // ignore
        return;
      } else {
        order = { ...order, ...collapseItems(order) };
      }
      newState[k].push(order);
    });
  }

  // Make sure everything is sorted by priority.
  const comparePriority = (o1, o2) => -1 * (o1.priority - o2.priority); // sort in descending order
  const compareFlashing = (o1, o2) => {
    const f1 = shouldFlashOrder(o1);
    const f2 = shouldFlashOrder(o2);
    if (f1 === f2) {
      return 0;
    } else if (f1 === true) {
      return -1;
    } else {
      // f2 === true
      return 1;
    }
  };
  var sortFnByState = {
    pending: [comparePriority],
    started: [
      compareFlashing,
      comparePriority,
      (o1, o2) => o1.pos_order_name.localeCompare(o2.pos_order_name),
    ],
    done: [],
  };
  Object.entries(sortFnByState).forEach(([state, sortFns]) => {
    newState[state].sort((order1, order2) => {
      for (const fn of sortFns) {
        const cmp = fn(order1, order2);
        if (cmp !== 0) {
          return cmp;
        }
      }
      if (order1.estimated_pickup_time !== order2.estimated_pickup_time) {
        return Date.parse(order1.estimated_pickup_time) - Date.parse(order2.estimated_pickup_time);
      }
      if (order1.create_time !== order2.create_time) {
        return Date.parse(order1.create_time) - Date.parse(order2.create_time);
      }
      return order1.id.localeCompare(order2.id);
    });
  });

  return newState;
};

const WebsocketDisconnectBanner = () => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  return (
    <Alert
      message={t("unable_to_connect")}
      description={t("retrying")}
      type="error"
      showIcon
      style={{
        borderRadius: token.borderRadiusLG,
        marginBottom: token.marginLG,
      }}
    />
  );
};

const shouldFlashOrder = (order) => {
  // Display flashing warning if an order is in started state for more than
  // 15 min. This indicates that perhaps user forgot to click "done", keeping
  // the customer waiting.
  const startTime =
    order.start_time != null && order.start_time !== "" ? dayjs(order.start_time) : null;
  const doneTime =
    order.done_time != null && order.done_time !== "" ? dayjs(order.done_time) : null;
  return doneTime === null && dayjs().toDate() > (startTime ?? dayjs()).add(15, "m").toDate();
};

export default () => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { getOrderContentFontSize } = useResponsiveFontSize();
  const screens = Grid.useBreakpoint();
  const restaurantId = sessionStorage.getItem("restaurant_id");
  if (!restaurantId) {
    return <Navigate to="/login" replace />;
  }

  const [ordersByState, setOrdersByState] = useState({
    pending: [],
    started: [],
    done: [],
  });
  const [activeView, setActiveView] = useState<"pending" | "started">("pending");
  const latestUpdateRef = useRef(null);

  const { disconnected: websocketDisconnect } = useReconnectingWebSocket({
    url: `${WS_URL}/v1/ws/r/${restaurantId}/orders`,
    onOpen: () => {
      // Clear state since the backend only returns the latest state, and our
      // local state could be very stale if we were disconnected.
      setOrdersByState({ pending: [], started: [], done: [] });
    },
    onMessage: (message) => {
      latestUpdateRef.current = Date.parse(message["ts"]);
      setOrdersByState((prev) => updateOrdersByState(prev, message));
    },
  });

  // Track which orders have auto-print disabled (both pending and started)
  const pendingOrderIds = ordersByState["pending"].map((order: any) => order.id);
  const startedOrderIds = ordersByState["started"].map((order: any) => order.id);
  const allOrderIds = [...pendingOrderIds, ...startedOrderIds];
  const autoPrintDisabledOrders = useLabelAutoPrintStatus(allOrderIds);

  const renderOrder = (order, totalItems, autoPrintDisabled?: boolean) => {
    const orderTypeConfig = getOrderTypeDisplay(order.pos_order_type);
    let orderPickupTime = null;
    if (order.estimated_pickup_time) {
      orderPickupTime = new Date(order.estimated_pickup_time);
    }

    const flashWarning = shouldFlashOrder(order);
    return (
      <div
        className={flashWarning ? "theme-blinker" : ""}
        style={{
          width: "100%",
        }}
      >
        <div
          style={{
            marginBottom: "6px",
            display: "flex",
            alignItems: "center",
            gap: token.marginSM,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontWeight: token.fontWeightStrong,
              fontSize: `calc(${getOrderContentFontSize()} * 1.2)`,
              color: flashWarning ? token.colorError : token.colorText,
              lineHeight: 1.2,
            }}
          >
            {order.pos_order_name ? order.pos_order_name : order.pos_order_id}
          </span>
          {order.pos_order_type && (
            <Tag
              color={orderTypeConfig?.color || undefined}
              style={{
                borderRadius: token.borderRadiusSM,
                fontSize: `calc(${getOrderContentFontSize()} * 0.85)`,
                padding: "2px 10px",
                margin: 0,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {orderTypeConfig?.logo && (
                <img
                  src={orderTypeConfig.logo}
                  alt={order.pos_order_type}
                  style={{
                    height: `calc(${getOrderContentFontSize()} * 0.85)`,
                    width: "auto",
                    verticalAlign: "middle",
                  }}
                />
              )}
              {order.pos_order_type}
            </Tag>
          )}
          {autoPrintDisabled && (
            <span
              title={t("auto_print_off")}
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PrinterOutlined
                style={{
                  color: token.colorTextTertiary,
                  fontSize: `calc(${getOrderContentFontSize()} * 1.1)`,
                }}
              />
              <span
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "2px",
                  background: token.colorTextTertiary,
                  transform: "rotate(-45deg)",
                }}
              />
            </span>
          )}
          {(totalItems === 0 || !order.pos_order_name) && (
            <ExclamationCircleOutlined
              style={{
                color: token.colorWarning,
                fontSize: `calc(${getOrderContentFontSize()} * 1.1)`,
              }}
            />
          )}
        </div>
        <div
          style={{
            marginBottom: "6px",
            color: token.colorTextSecondary,
            fontSize: getOrderContentFontSize(),
            display: "flex",
            alignItems: "center",
            gap: token.marginSM,
          }}
        >
          <span style={{ fontWeight: token.fontWeightStrong }}>
            {totalItems} {totalItems === 1 ? "item" : "items"}
          </span>
          {order.customer && order.customer.name && (
            <>
              <span>•</span>
              <span>{order.customer.name}</span>
            </>
          )}
        </div>
        {order.notes && (
          <Paragraph
            ellipsis={{ rows: 1, expandable: false }}
            style={{
              margin: 0,
              marginBottom: "6px",
              fontSize: `calc(${getOrderContentFontSize()} * 0.9)`,
              color: token.colorTextTertiary,
              lineHeight: 1.4,
            }}
          >
            {order.notes}
          </Paragraph>
        )}
        {orderPickupTime && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              color: token.colorInfo,
              fontSize: `calc(${getOrderContentFontSize()} * 0.95)`,
              fontWeight: token.fontWeightStrong,
            }}
          >
            <ClockCircleOutlined
              style={{ fontSize: `calc(${getOrderContentFontSize()} * 1.05)` }}
            />
            <span>{shortDisplayTime(orderPickupTime)}</span>
          </div>
        )}
      </div>
    );
  };

  const renderCollapsedOrder = (order) => {
    const totalItems =
      order.items && order.items.length > 0
        ? order.items.reduce((acc, item) => acc + item.qty, 0)
        : 0;
    const autoPrintDisabled = autoPrintDisabledOrders.has(order.id);
    return renderOrder(order, totalItems, autoPrintDisabled);
  };

  // Wrapper for started orders to include auto-print status
  const renderStartedOrderWithStatus = (order, totalItems) => {
    const autoPrintDisabled = autoPrintDisabledOrders.has(order.id);
    return renderOrder(order, totalItems, autoPrintDisabled);
  };

  const renderOrderTitle = (order) => (
    <span
      style={{
        fontSize: `calc(${getOrderContentFontSize()} * 1.25)`,
        fontWeight: token.fontWeightStrong,
        color: token.colorText,
      }}
    >
      {t("order")} {order.pos_order_name ? order.pos_order_name : order.pos_order_id}
    </span>
  );

  return (
    <>
      {websocketDisconnect && <WebsocketDisconnectBanner />}

      <Flex vertical style={{ minHeight: "calc(100vh - 96px)" }}>
        {screens.md ? (
          // Desktop layout: Two columns side by side
          <Row gutter={{ xs: 8, sm: 12, md: 16, lg: 24 }} style={{ flex: 1, minHeight: 0 }}>
            {/* LEFT SIDE - PENDING ORDERS */}
            <Col xs={24} sm={10} md={10} lg={10}>
              <Flex vertical style={{ height: "100%" }}>
                <Card
                  style={{
                    flex: 1,
                    borderRadius: token.borderRadiusLG,
                    boxShadow: token.boxShadowSecondary,
                    overflow: "hidden",
                  }}
                  styles={{ body: { padding: 0, height: "100%" } }}
                >
                  <PendingOrderTabs
                    pendingOrders={ordersByState["pending"]}
                    doneOrders={ordersByState["done"]}
                    restaurantId={restaurantId}
                    renderOrder={renderCollapsedOrder}
                    renderOrderTitle={renderOrderTitle}
                  />
                </Card>
              </Flex>
            </Col>

            {/* RIGHT SIDE - STARTED ORDERS */}
            <Col xs={24} sm={14} md={14} lg={14}>
              <Flex vertical style={{ height: "100%" }}>
                <StartedOrderCol
                  orders={ordersByState["started"]}
                  renderOrder={renderStartedOrderWithStatus}
                  renderOrderTitle={renderOrderTitle}
                  restaurantId={restaurantId}
                  showItemSelect={true}
                />
              </Flex>
            </Col>
          </Row>
        ) : (
          // Mobile layout: Navigation bar to switch between sections
          <Flex vertical gap="middle" style={{ flex: 1 }}>
            {/* Navigation Segmented Control */}
            <Segmented
              value={activeView}
              onChange={(value) => setActiveView(value as "pending" | "started")}
              block
              size="large"
              options={[
                {
                  label: `${t("pending_orders")} (${ordersByState["pending"].length})`,
                  value: "pending",
                },
                {
                  label: `${t("in_progress")} (${ordersByState["started"].length})`,
                  value: "started",
                },
              ]}
              style={{
                background: token.colorBgContainer,
                padding: token.paddingXS,
                borderRadius: token.borderRadiusLG,
                boxShadow: token.boxShadowTertiary,
              }}
            />

            {/* Content Area */}
            {activeView === "pending" ? (
              <Flex vertical style={{ flex: 1 }}>
                <Card
                  style={{
                    flex: 1,
                    borderRadius: token.borderRadiusLG,
                    boxShadow: token.boxShadowSecondary,
                    overflow: "hidden",
                  }}
                  styles={{ body: { padding: 0, height: "100%" } }}
                >
                  <PendingOrderTabs
                    pendingOrders={ordersByState["pending"]}
                    doneOrders={ordersByState["done"]}
                    restaurantId={restaurantId}
                    renderOrder={renderCollapsedOrder}
                    renderOrderTitle={renderOrderTitle}
                  />
                </Card>
              </Flex>
            ) : (
              <Flex vertical style={{ flex: 1 }}>
                <StartedOrderCol
                  orders={ordersByState["started"]}
                  renderOrder={renderStartedOrderWithStatus}
                  renderOrderTitle={renderOrderTitle}
                  restaurantId={restaurantId}
                  showItemSelect={true}
                />
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
    </>
  );
};
